import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { AlertTriangle, DollarSign, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/config";

/**
 * ChangePaymentStatusDialog — iter225 (2026-03-02)
 *
 * Admin-only manual override of a booking's `payment_status`. Owner
 * directive: "This is needed so I can manually correct incorrect
 * payment statuses, especially future recurring bookings that were
 * incorrectly marked PAID. Changing the status manually must NOT
 * create a Stripe charge, NOT charge the customer, NOT create or
 * modify a Stripe PaymentIntent."
 *
 * Only three targets are exposed (owner list): PAID, UNPAID,
 * PAYMENT_PENDING. The backend endpoint `/admin/bookings/{id}/
 * set-payment-status` handles audit + strict Stripe isolation.
 */
const OPTIONS = [
  { key: "paid",             label: "Paid",             sub: "Mark this booking as paid (no Stripe charge fires).", tone: "emerald" },
  { key: "payment_pending",  label: "Payment Pending",  sub: "Awaiting the customer's payment. Becomes remind-eligible.", tone: "amber" },
  { key: "unpaid",           label: "Unpaid",           sub: "Reset to unpaid. Becomes remind-eligible if the service is completed.", tone: "gray" },
];

const TONE_CLASSES = {
  emerald: "border-emerald-300 bg-emerald-50 text-emerald-800",
  amber:   "border-amber-300 bg-amber-50 text-amber-900",
  gray:    "border-gray-300 bg-gray-50 text-gray-800",
};

export default function ChangePaymentStatusDialog({
  open, onOpenChange, booking, onChanged,
}) {
  const current = String(booking?.payment_status || "unpaid").toLowerCase();
  const [target, setTarget] = useState(current === "paid" ? "unpaid" : "paid");
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  // When the dialog opens, reset the choice to something DIFFERENT
  // from the current status so the "Save" button is meaningful.
  useEffect(() => {
    if (!open) { setConfirming(false); setReason(""); return; }
    setTarget(current === "paid" ? "unpaid" : "paid");
    setConfirming(false);
    setReason("");
  }, [open, current]);

  const submit = async () => {
    if (target === current) {
      toast.info("Already at that status.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/bookings/${booking.booking_id}/set-payment-status`,
        { payment_status: target, reason: reason.trim() || undefined },
      );
      toast.success(
        `Payment status → ${data.payment_status.replace("_", " ").toUpperCase()}`,
      );
      onChanged?.(data);
      onOpenChange?.(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not update payment status",
      );
    } finally {
      setBusy(false);
    }
  };

  if (!booking) return null;
  const targetOpt = OPTIONS.find((o) => o.key === target) || OPTIONS[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="change-payment-status-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Outfit] tracking-tight">
            <DollarSign className="w-5 h-5 text-blue-700" />
            Change Payment Status
          </DialogTitle>
          <DialogDescription className="text-sm">
            Booking <span className="font-mono text-gray-700">#{(booking.booking_id || "").slice(-6)}</span>
            {" "}· {booking.service_name || "Service"}
            {booking.preferred_date && (<> · {booking.preferred_date}</>)}
          </DialogDescription>
        </DialogHeader>

        {!confirming ? (
          <>
            <div className="space-y-2 mt-1">
              <div className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
                Current: <span className="text-gray-800">{current.replace("_", " ").toUpperCase()}</span>
              </div>
              {OPTIONS.map((o) => (
                <label
                  key={o.key}
                  className={`flex items-start gap-3 p-3 rounded-2xl border-2 cursor-pointer transition-all ${
                    target === o.key
                      ? TONE_CLASSES[o.tone]
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  data-testid={`change-payment-status-option-${o.key}`}
                >
                  <input
                    type="radio"
                    name="payment-status"
                    value={o.key}
                    checked={target === o.key}
                    onChange={() => setTarget(o.key)}
                    className="mt-1 w-4 h-4 accent-emerald-700"
                    data-testid={`change-payment-status-radio-${o.key}`}
                  />
                  <div className="min-w-0">
                    <div className="font-extrabold text-sm">{o.label}</div>
                    <div className="text-[12px] text-gray-600 leading-relaxed">{o.sub}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-3">
              <label className="text-[11px] uppercase tracking-[0.14em] font-extrabold text-gray-500">
                Reason (optional)
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Future booking incorrectly marked paid"
                className="mt-1 w-full h-10 px-3 rounded-xl border-2 border-gray-200 focus:border-emerald-400 outline-none text-sm"
                data-testid="change-payment-status-reason"
                maxLength={500}
              />
            </div>
          </>
        ) : (
          <div className="mt-1 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
                data-testid="change-payment-status-confirm">
            <div className="flex items-center gap-2 font-extrabold">
              <AlertTriangle className="w-4 h-4" /> Confirm manual change
            </div>
            <div className="mt-2 leading-relaxed">
              Change payment status of{" "}
              <span className="font-bold">#{(booking.booking_id || "").slice(-6)}</span>{" "}
              from <span className="font-bold uppercase">{current.replace("_", " ")}</span>{" "}
              to <span className="font-bold uppercase">{targetOpt.label}</span>?
            </div>
            <ul className="mt-2 text-[12px] list-disc pl-5 space-y-0.5">
              <li>No Stripe charge is created.</li>
              <li>The customer is NOT charged.</li>
              <li>No PaymentIntent is created or modified.</li>
              <li>The saved card and AutoPay stay untouched.</li>
              <li>This action is stamped in the booking&apos;s audit history.</li>
            </ul>
            {reason.trim() && (
              <div className="mt-2 text-[12px]">
                Reason: <span className="italic">{reason.trim()}</span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={busy}
            data-testid="change-payment-status-cancel"
          >
            Cancel
          </Button>
          {!confirming ? (
            <Button
              onClick={() => setConfirming(true)}
              disabled={target === current}
              className="bg-blue-700 hover:bg-blue-800 text-white font-extrabold"
              data-testid="change-payment-status-review"
            >
              Review change
            </Button>
          ) : (
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setConfirming(false)}
                disabled={busy}
                data-testid="change-payment-status-back"
              >
                Back
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold"
                data-testid="change-payment-status-save"
              >
                {busy ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
                ) : (
                  <>Save change</>
                )}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
